import { useMemo, useState } from 'react';
import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  SelectField,
  toast,
} from '@eon/ui';
import { hooks } from '@eon/api-client';
import type { Device, Station } from '@eon/types';
import { Search } from 'lucide-react';

const PAGE_SIZE = 50;

interface AddStationDeviceDialogProps {
  station: Station;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddStationDeviceDialog({ station, open, onOpenChange }: AddStationDeviceDialogProps) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [deviceId, setDeviceId] = useState<string | undefined>(undefined);
  const list = hooks.useAdminDevices({ q, page, pageSize: PAGE_SIZE });
  const add = hooks.useAddStationDevice();

  const linkedIds = useMemo(
    () => new Set((station.devices ?? []).map((d) => d.id)),
    [station.devices],
  );

  const candidates = useMemo(() => {
    const rows = list.data?.data ?? [];
    return rows.filter((d) => {
      if (linkedIds.has(d.id)) return false;
      if (station.organizationId) {
        if (d.organizationId && d.organizationId !== station.organizationId) return false;
      }
      return true;
    });
  }, [list.data?.data, linkedIds, station.organizationId]);

  const selected = candidates.find((d) => d.id === deviceId);

  const options = candidates.map((d) => ({
    value: d.id,
    label: d.displayName?.trim() || d.serialNumber,
    description: stationLabelForDevice(d, t),
  }));

  const submit = async () => {
    if (!deviceId) {
      toast.error(t('station.link.selectRequired'));
      return;
    }
    try {
      await add.mutateAsync({ stationId: station.id, deviceId });
      toast.success(t('station.link.linked'));
      setDeviceId(undefined);
      setQ('');
      onOpenChange(false);
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('station.link.addTitle')}</DialogTitle>
          <DialogDescription>{t('station.link.addDescription', { name: station.name })}</DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <Input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
              setDeviceId(undefined);
            }}
            placeholder={t('actions.search')}
            className="pl-9"
          />
        </div>

        <SelectField
          value={deviceId}
          onValueChange={setDeviceId}
          options={options}
          placeholder={t('station.link.selectPlaceholder')}
          disabled={list.isLoading || options.length === 0}
        />

        {selected?.stationId && selected.stationId !== station.id ? (
          <p className="text-amber-600 dark:text-amber-400 text-sm">
            {t('station.link.reassignWarning')}
          </p>
        ) : null}

        {list.data && list.data.totalPages > 1 ? (
          <div className="text-muted-foreground flex items-center justify-between text-xs">
            <span>
              {t('pagination.page')} {page} {t('pagination.of')} {list.data.totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {t('pagination.previous')}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= (list.data.totalPages ?? 1)}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('pagination.next')}
              </Button>
            </div>
          </div>
        ) : null}

        {!list.isLoading && options.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('station.link.noCandidates')}</p>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('actions.cancel')}
          </Button>
          <Button type="button" onClick={submit} disabled={add.isPending || !deviceId}>
            {add.isPending ? t('station.link.linking') : t('station.link.link')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function stationLabelForDevice(
  d: Device,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string | undefined {
  if (!d.stationId) return d.serialNumber;
  return `${d.serialNumber} · ${t('station.link.currentlyElsewhere')}`;
}
